import BasicDataGridV2 from 'components/generic/datagrid/BasicDataGridV2'
import { GS_ROLE, GS_ROLE_INFO } from 'utils/constants'
import { GridEnrichedColDef } from '@mui/x-data-grid-pro_modern'
import { useTheme } from '@mui/styles'
import { Avatar, AvatarGroup, Box, Typography } from '@mui/material_modern'
import { ChevronRight } from '@mui/icons-material_modern'
import { useGetGsUserListQuery } from 'services/gsUsers'
import Loading from 'components/generic/Loading'
import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { showFormModal } from 'state/ui/index'

const getGridStyles = theme => ({
  border: 'none',
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    padding: theme.spacing(1),
  },
  '& .MuiDataGrid-columnHeader': {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  '& .MuiDataGrid-cell': {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  '& .MuiDataGrid-columnSeparator': {
    visibility: 'hidden',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-row': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer', // Change cursor to pointer on hover
  },
})

const RolesAndPermissionsContainer = () => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const { data: usersData, isLoading: isLoadingUsers } = useGetGsUserListQuery({
    page: 1,
    pageSize: 500,
  })

  const columns: GridEnrichedColDef[] = [
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: theme.spacing(1),
          }}
        >
          {row.role}
        </Typography>
      ),
    },
    {
      field: 'users',
      headerName: 'Users',
      flex: 2,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <AvatarGroup max={4}>
              {row.users.map((user, index) => (
                <Avatar key={index}>
                  {user.firstName.charAt(0).toUpperCase() + user.lastName.charAt(0).toUpperCase()}
                </Avatar>
              ))}
            </AvatarGroup>
          </Box>
        )
      },
    },
    {
      field: 'navigate',
      headerName: '',
      flex: 0.2,
      sortable: false,
      renderCell: () => (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '50%',
            transition: 'background-color 0.2s, outline 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(0, 123, 255, 0.1)',
              outline: `2px solid ${theme.palette.primary.main}`, // Outline on hover
            },
          }}
        >
          <ChevronRight sx={{ color: 'text.secondary' }} />
        </Box>
      ),
    },
  ]

  //? Map GS_ROLE_INFO to DataGrid rows, linking users based on greensparkRole
  const rows = useMemo(() => {
    if (!usersData?.Items) return []

    const rolesToShow = Object.keys(GS_ROLE_INFO).filter(
      roleKey => roleKey !== GS_ROLE.PORTAL_USER && roleKey !== GS_ROLE.YARD_ADMIN,
    )

    return rolesToShow.map(roleKey => {
      const roleInfo = GS_ROLE_INFO[roleKey]
      const usersInRole = usersData.Items.filter(
        user => user.greensparkRole === roleInfo.id, // Match role ID
      )
      return {
        id: roleInfo.id,
        role: roleInfo.name,
        users: usersInRole,
      }
    })
  }, [usersData])

  const handleRowClick = params => {
    const { id } = params.row
    dispatch(showFormModal({ formModalName: 'configure-role-blade', formModalProps: { roleId: id } }))
  }

  return (
    <>
      {isLoadingUsers ? (
        <Loading localized />
      ) : (
        <BasicDataGridV2
          columns={columns}
          rows={rows}
          sx={getGridStyles(theme)}
          hideFooter
          onRowClick={handleRowClick}
        ></BasicDataGridV2>
      )}
    </>
  )
}

export default RolesAndPermissionsContainer
